import type { RegionSourceOverlapResponse, RegionSourceOverlapPair } from '../types/index.js';
import { Icon } from './ui/Icon.js';
import { CHART_ACTION, CHART_INK } from './chartTheme.js';

/**
 * Spec 171 — region source-overlap view.
 *
 * Renders the set overlap of CITED domains between regions: how many domains are
 * shared vs unique to each region, plus the overlap %. This surfaces the striking
 * insight that two regions' AI source ecosystems can barely overlap — i.e. a US
 * user and a Danish user are being cited completely different sources.
 *
 * Defensive: renders nothing when there are no pairs (a single-region brand never
 * produces a pair, so this panel simply doesn't appear).
 */

function regionName(
  code: string,
  perRegion: RegionSourceOverlapResponse['per_region'],
): string {
  // per_region only carries codes + counts; the code is the display label here.
  return perRegion.find((r) => r.region_code === code)?.region_code ?? code;
}

/**
 * Spec 234 §5: monochrome — "shared" is the number this whole panel exists
 * to show (how much of two regions' source ecosystems overlap), so it alone
 * takes the action colour; the two region-only counts are the plain ink
 * band either side of it. No categorical palette; the legend row below
 * still names each segment directly.
 */
function OverlapBar({ pair }: { pair: RegionSourceOverlapPair }) {
  const total = pair.a_only + pair.shared + pair.b_only;
  const pct = (n: number) => (total > 0 ? (n / total) * 100 : 0);
  const overlapPct = Math.round(pair.overlap_pct);

  return (
    <div className="space-y-2" data-testid="region-overlap-pair">
      <div className="flex items-center justify-between text-xs">
        <span className="font-medium text-slate-700">
          {pair.region_a} vs {pair.region_b}
        </span>
        <span className="text-slate-500">
          {overlapPct}% shared sources
        </span>
      </div>

      {/* Stacked bar: region-A-only · shared · region-B-only */}
      <div className="flex h-5 w-full overflow-hidden rounded-full" style={{ backgroundColor: 'var(--hub-color-band)' }}>
        <div
          className="h-full"
          style={{ width: `${pct(pair.a_only)}%`, backgroundColor: CHART_INK, opacity: 0.35 }}
          title={`${pair.a_only} domains only cited in ${pair.region_a}`}
        />
        <div
          className="h-full"
          style={{ width: `${pct(pair.shared)}%`, backgroundColor: CHART_ACTION }}
          title={`${pair.shared} domains cited in both regions`}
        />
        <div
          className="h-full"
          style={{ width: `${pct(pair.b_only)}%`, backgroundColor: CHART_INK, opacity: 0.2 }}
          title={`${pair.b_only} domains only cited in ${pair.region_b}`}
        />
      </div>

      {/* Legend / counts */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
        <span className="flex items-center gap-1">
          <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: CHART_INK, opacity: 0.35 }} />
          {pair.region_a} only: <span className="font-medium text-slate-700">{pair.a_only}</span>
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: CHART_ACTION }} />
          Shared: <span className="font-medium text-slate-700">{pair.shared}</span>
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: CHART_INK, opacity: 0.2 }} />
          {pair.region_b} only: <span className="font-medium text-slate-700">{pair.b_only}</span>
        </span>
      </div>
    </div>
  );
}

export function RegionSourceOverlap({
  overlap,
}: {
  overlap: RegionSourceOverlapResponse | null;
}) {
  if (!overlap || overlap.pairs.length === 0) return null;

  return (
    <section className="mt-8" data-testid="region-source-overlap">
      <div className="flex items-center gap-2">
        <Icon name="bx-layer" className="text-violet-600" />
        <h2 className="text-lg font-semibold text-slate-900">Source ecosystems by region</h2>
      </div>
      <p className="mt-1 text-xs text-slate-400">
        Which cited domains overlap across regions — and which are unique to one market.
        A low overlap means AI is citing a different set of sources to each region.
      </p>

      {/* Spec 234: no border on this rounded-lg wrapper — combined with the
          14px sheet radius that's exactly gate 231.A.2's card residue. */}
      <div className="rounded-lg bg-white mt-3 space-y-5 p-5">
        {/* Per-region cited-domain counts */}
        {overlap.per_region.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {overlap.per_region.map((r) => (
              <span
                key={r.region_code}
                className="inline-flex items-center gap-1.5 rounded-md bg-slate-50 px-2.5 py-1 text-xs text-slate-600"
              >
                <span className="font-semibold text-slate-800">{r.region_code}</span>
                <span className="text-slate-400">·</span>
                {r.cited_domain_count} cited {r.cited_domain_count === 1 ? 'domain' : 'domains'}
              </span>
            ))}
          </div>
        )}

        {/* Pairwise overlap bars */}
        <div className="space-y-5">
          {overlap.pairs.map((pair) => (
            <OverlapBar
              key={`${pair.region_a}-${pair.region_b}`}
              pair={{
                ...pair,
                region_a: regionName(pair.region_a, overlap.per_region),
                region_b: regionName(pair.region_b, overlap.per_region),
              }}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
